import { useState, useEffect } from 'react'
import { idpSSO, authAPI } from '../services/api'
import { useAuth } from '../hooks/useAuth'

interface DevUser {
  id: number
  email: string
  name: string | null
  is_superadmin: boolean
}

interface DevCompany {
  id: number
  name: string
  slug: string
}

interface DevRole {
  user_id: number
  company_id: number
  role: string
}

export default function Login() {
  const { login } = useAuth()
  const [devMode, setDevMode] = useState(false)
  const [devUsers, setDevUsers] = useState<DevUser[]>([])
  const [devCompanies, setDevCompanies] = useState<DevCompany[]>([])
  const [devRoles, setDevRoles] = useState<DevRole[]>([])
  const [selectedUser, setSelectedUser] = useState<number | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [devAvailable, setDevAvailable] = useState(false)

  // Check if dev mode is available
  useEffect(() => {
    authAPI.devUsers().then(() => setDevAvailable(true)).catch(() => {})
  }, [])

  const handleDevLogin = async () => {
    if (!selectedUser || !selectedCompany) return
    setLoading(true)
    setError('')
    try {
      const resp = await authAPI.devLogin(selectedUser, selectedCompany)
      await login(resp.data.token)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error en dev login')
    } finally {
      setLoading(false)
    }
  }

  const loadDevData = async () => {
    try {
      const resp = await authAPI.devUsers()
      setDevUsers(resp.data.users)
      setDevCompanies(resp.data.companies)
      setDevRoles(resp.data.roles)
      setDevMode(true)
      if (resp.data.users.length > 0) setSelectedUser(resp.data.users[0].id)
      if (resp.data.companies.length > 0) setSelectedCompany(resp.data.companies[0].id)
    } catch {
      setError('No hay datos de desarrollo disponibles. Ejecuta el seed primero.')
    }
  }

  const getAvailableCompanies = (userId: number) => {
    const companyIds = devRoles.filter(r => r.user_id === userId).map(r => r.company_id)
    return devCompanies.filter(c => companyIds.includes(c.id))
  }

  return (
    <div className="login-page">
      <div className="login-orb"></div>
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">
            <i className="bi bi-file-text"></i>
          </div>
          <div className="login-logo-name">Adder Contracts</div>
        </div>
        <h1>Gestión de Contratos</h1>
        <p className="login-subtitle">
          Plataforma centralizada de gestión y firma de contratos<br />
          <small>Powered by L6 Ecosystem</small>
        </p>

        {/* ── IDP Login ─────────────────────────────── */}
        <button className="btn btn-primary btn-lg w-100 mb-3" onClick={() => window.location.href = idpSSO.getLoginUrl()}>
          <i className="bi bi-shield-lock me-2"></i>
          Iniciar sesión con Adder IdP
        </button>

        {/* ── Dev Login ─────────────────────────────── */}
        {devAvailable && !devMode && (
          <div>
            <div className="login-divider"><span>o</span></div>
            <button className="btn btn-outline-secondary w-100" onClick={loadDevData}>
              <i className="bi bi-laptop me-2"></i>
              Modo desarrollo (sin IdP)
            </button>
          </div>
        )}

        {devMode && (
          <div className="dev-login-form">
            <div className="login-divider"><span>Desarrollo</span></div>

            <div className="mb-3 text-start">
              <label className="form-label">Usuario</label>
              <select className="form-select" value={selectedUser || ''}
                onChange={(e) => {
                  const uid = Number(e.target.value)
                  setSelectedUser(uid)
                  // Auto-select first available company
                  const available = getAvailableCompanies(uid)
                  if (available.length > 0) setSelectedCompany(available[0].id)
                }}>
                {devUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email} {u.is_superadmin ? '⭐' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3 text-start">
              <label className="form-label">Empresa</label>
              <select className="form-select" value={selectedCompany || ''}
                onChange={(e) => setSelectedCompany(Number(e.target.value))}>
                {(selectedUser ? getAvailableCompanies(selectedUser) : devCompanies).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {error && <div className="alert alert-danger py-2 small">{error}</div>}

            <button className="btn btn-success w-100" onClick={handleDevLogin} disabled={loading || !selectedUser || !selectedCompany}>
              {loading ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-key me-2"></i>}
              Acceder en modo desarrollo
            </button>

            <button className="btn btn-sm btn-outline-secondary w-100 mt-2" onClick={() => setDevMode(false)}>
              Volver al login normal
            </button>
          </div>
        )}
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg, #07090f);
          position: relative;
          overflow: hidden;
        }
        .login-orb {
          position: absolute;
          width: 600px;
          height: 600px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%);
          top: -200px;
          right: -200px;
          pointer-events: none;
        }
        .login-card {
          background: var(--surface, #0d0f1a);
          border: 1px solid var(--border, rgba(255,255,255,0.07));
          border-radius: 16px;
          padding: 2.5rem;
          max-width: 440px;
          width: 100%;
          text-align: center;
          box-shadow: 0 24px 80px rgba(0,0,0,0.4);
          position: relative;
          backdrop-filter: blur(20px);
        }
        .login-logo {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        .login-logo-icon {
          width: 52px;
          height: 52px;
          border-radius: 12px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 22px;
          box-shadow: 0 4px 20px rgba(99,102,241,0.3);
        }
        .login-logo-name {
          font-family: Arial, sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: var(--text-muted, rgba(255,255,255,0.35));
          letter-spacing: 2px;
          text-transform: uppercase;
        }
        .login-card h1 {
          font-size: 22px;
          font-weight: 600;
          color: var(--text, #e2e8f0);
          margin-bottom: 0.3rem;
        }
        .login-subtitle {
          font-size: 13px;
          color: var(--text-sub, rgba(255,255,255,0.55));
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }
        .login-subtitle small {
          color: var(--text-muted, rgba(255,255,255,0.35));
        }
        .login-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 1rem 0;
          color: var(--text-muted, rgba(255,255,255,0.25));
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .login-divider::before,
        .login-divider::after {
          content: '';
          flex: 1;
          border-bottom: 1px solid var(--border, rgba(255,255,255,0.07));
        }
        .dev-login-form {
          margin-top: 0.5rem;
        }
      `}</style>
    </div>
  )
}
